import { useState, useEffect, useCallback } from 'react';
import { useCashflow } from '../context/CashflowContext';
import { cashflowAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Plus, Trash2, Users, Crown, Mail } from 'lucide-react';
import { toast } from 'sonner';

export default function TeamPage() {
  const { activeCashflow, loading } = useCashflow();
  const [members, setMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [inviting, setInviting] = useState(false);

  const loadMembers = useCallback(async () => {
    if (!activeCashflow) return;
    setLoadingMembers(true);
    try {
      const response = await cashflowAPI.getMembers(activeCashflow.id);
      setMembers(response.data);
    } catch (error) {
      console.error('Error loading members:', error);
    } finally {
      setLoadingMembers(false);
    }
  }, [activeCashflow]);

  useEffect(() => {
    if (activeCashflow) {
      loadMembers();
    }
  }, [activeCashflow, loadMembers]);

  const handleInvite = async (e) => {
    e.preventDefault();
    
    if (!email.trim()) {
      toast.error('Please enter an email address');
      return;
    }

    setInviting(true);
    try {
      await cashflowAPI.addMember(activeCashflow.id, email);
      toast.success('Team member added!');
      setEmail('');
      setIsDialogOpen(false);
      loadMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error adding team member');
    } finally {
      setInviting(false);
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (window.confirm('Are you sure you want to remove this team member?')) {
      try {
        await cashflowAPI.removeMember(activeCashflow.id, memberId);
        toast.success('Team member removed');
        loadMembers();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Error removing team member');
      }
    }
  };

  if (loading || !activeCashflow) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="team-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-[#021d49]" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Team
          </h1>
          <p className="text-stone-500 mt-1">
            Manage team members for {activeCashflow.name}
          </p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              className="bg-[#021d49] hover:bg-[#032d6b] text-white rounded-[4px]"
              data-testid="add-member-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Member
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[400px]">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                Add Team Member
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleInvite} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Email Address</Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="team@example.com"
                  className="rounded-[4px]"
                  data-testid="member-email-input"
                />
                <p className="text-xs text-stone-500">
                  The user must already have a Solara account
                </p>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsDialogOpen(false)}
                  className="rounded-[4px]"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={inviting}
                  className="bg-[#021d49] hover:bg-[#032d6b] text-white rounded-[4px]"
                  data-testid="invite-member-btn"
                >
                  {inviting ? 'Adding...' : 'Add Member'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Team Members */}
      <Card className="bg-white border border-stone-200 rounded-lg shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-stone-900 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <Users className="w-5 h-5 text-stone-400" />
            Team Members ({members.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loadingMembers ? (
            <div className="flex items-center justify-center py-8">
              <div className="spinner" />
            </div>
          ) : members.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 mx-auto text-stone-300 mb-3" />
              <p className="text-stone-500">No team members yet</p>
              <p className="text-sm text-stone-400">Add team members to collaborate on this cashflow</p>
            </div>
          ) : (
            <div className="space-y-4">
              {members.map((member) => (
                <div 
                  key={member.id}
                  className="team-member-card"
                  data-testid={`member-${member.id}`}
                >
                  <div className="w-12 h-12 rounded-full bg-stone-200 flex items-center justify-center text-stone-600 font-medium">
                    {member.name?.charAt(0)?.toUpperCase() || 'U'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium text-stone-900">{member.name}</h3>
                      {member.is_owner && (
                        <Badge variant="secondary" className="bg-amber-100 text-amber-800">
                          <Crown className="w-3 h-3 mr-1" />
                          Owner
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-stone-500 flex items-center gap-1">
                      <Mail className="w-3 h-3" />
                      {member.email}
                    </p>
                  </div>
                  {!member.is_owner && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleRemoveMember(member.id)}
                      className="text-stone-400 hover:text-red-600"
                      data-testid={`remove-member-${member.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
